import { signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import type { ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";

import { useLayout } from "@/contexts/layout-context";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { cn } from "@/utils/lib";

let lastScrollY = typeof window !== "undefined" ? window.scrollY : 0;

const handleScroll = () => {
  const currentScrollY = window.scrollY;
  if (currentScrollY < lastScrollY) {
    scrollDirectionSignal.value = "up";
  } else {
    scrollDirectionSignal.value = "down";
  }
  lastScrollY = Math.max(0, currentScrollY);
};

let hasScrollListener = false;

const scrollDirectionSignal = signal("up");

let observedElements: HTMLElement[] = [];

type StickyStackProps = {
  offset?: number;
};

export const StickyStack = ({
  offset = 0,
  ...props
}: ComponentProps<"div"> & StickyStackProps) => {
  useSignals();
  const { safeInsetTop } = useLayout();

  const elementRef = useRef<HTMLDivElement>(null!);

  let translateOffset = offset;
  for (const element of observedElements) {
    if (element === elementRef.current) break;
    const elementOffset = parseInt(element.dataset.offset!);
    translateOffset += element.offsetHeight + elementOffset;
  }

  const isUp = scrollDirectionSignal.value === "up";
  const [height, setHeight] = useState(0);
  const { ref: stickyObserverRef, isIntersecting } = useIntersectionObserver({
    initialIsIntersecting: lastScrollY === 0,
    // This will make sure we won't trigger sticky while the element is actually below the fold.
    rootMargin: `0px 0px 1000px 0px`,
    threshold: 0,
  });

  // Attach scroll listener, but only once
  // We don't use context to avoid full tree re-renders
  useEffect(() => {
    if (hasScrollListener) {
      return;
    }
    hasScrollListener = true;
    document.addEventListener("scroll", handleScroll, { passive: true });
  }, []);

  // Attach resize observer
  useEffect(() => {
    const element = elementRef.current;

    const observer = new ResizeObserver((entries) => {
      const newHeight = (
        entries.find((entry) => entry.target === element)?.target as HTMLElement
      )?.offsetHeight;

      if (newHeight) {
        setHeight(newHeight);
      }
    });

    observer.observe(element);

    // We need to make sure the elements are stored in the array in the
    // same order as they appear in the DOM.
    observedElements = [...observedElements, element].sort((a, b) => {
      if (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) {
        return -1;
      } else if (
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING
      ) {
        return 1;
      }
      return 0;
    });

    return () => {
      observer.disconnect();
      observedElements.splice(observedElements.indexOf(element), 1);
    };
  }, []);

  useEffect(() => {
    const el = elementRef.current;

    if (isIntersecting) {
      el.style.position = "static";
      el.style.transform = `translateY(0)`;
      el.style.transitionDuration = "0s";
    } else if (isUp) {
      el.style.transform = `translateY(0)`;
      el.style.transitionDuration = "0.3s";
      el.style.position = "sticky";
    } else {
      el.style.transform = `translateY(${(height + translateOffset + safeInsetTop) * -1}px)`;
      el.style.transitionDuration =
        el.style.position === "sticky" ? "0.3s" : "0s";
      el.style.position = "sticky";
    }
  }, [height, isIntersecting, isUp, safeInsetTop, translateOffset]);

  const stickyProps = {
    ref: elementRef,
    className: cn(props.className, "translate-y-0"),
    style: {
      ...props.style,
      top: translateOffset,
    },
    "data-offset": offset,
  };

  return (
    <>
      <div {...props} {...stickyProps} />
      <div
        ref={stickyObserverRef}
        style={{
          width: "100%",
          height,
          marginTop: height * -1,
          zIndex: -1,
          position: "relative",
          top: isIntersecting ? 0 : (height + translateOffset) * -1,
        }}
      />
    </>
  );
};
